import { useState, useEffect, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserDetails } from '@/components/users/user-details';
import { UserPayments } from '@/components/users/user-payments';
import { UserHealthDashboard } from '@/components/users/user-health-dashboard';
import { UserExercisePlans } from '@/components/users/user-exercise-plans';
import { UserCheckInTable } from '@/components/users/user-check-in-table';
import { ArrowLeftIcon, QrCode } from 'lucide-react';
import { apiRequest } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';
import { AssignPlanModal } from '@/components/shared/assign-plan-modal';
import { AssignDietPlanV2Modal } from '@/components/shared/assign-diet-plan-v2-modal';
import { AddPaymentDialog } from '@/components/payments/add-payment-dialog';
import { LoginQRDialog } from '@/components/shared/login-qr-dialog';

function UserDetailContent() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const userId = searchParams.get('id') as string;
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAssignDiet, setShowAssignDiet] = useState(false);
  const [showAssignExercise, setShowAssignExercise] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [qrUser, setQrUser] = useState<{ id: string; name: string } | null>(
    null
  );

  const fetchUser = async () => {
    if (!userId) return;
    setIsLoading(true);
    try {
      const userData = await apiRequest({
        method: 'GET',
        url: `/users/${userId}`,
      });

      if (userData) {
        console.log('Fetched user data:', userData);
        setUser(userData);
      } else {
        toast({
          title: t('common.error'),
          description: t('users.userNotFound'),
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      toast({
        title: t('common.error'),
        description: t('users.userNotFound'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchUser();
    } else {
      setIsLoading(false);
    }
  }, [userId, toast, t]);

  if (!userId) {
    return (
      <div className="flex flex-col items-center justify-center h-40">
        <p className="text-lg font-medium mb-4">{t('users.userNotFound')}</p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          {t('common.cancel')}
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-40">
        {t('common.loading')}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-40">
        <p className="text-lg font-medium mb-4">{t('users.userNotFound')}</p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          {t('common.cancel')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 flex-wrap">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
        <h1 className="text-3xl font-bold">
          {user.name} {user.surname}
        </h1>
        <div className="flex gap-2 ml-auto flex-wrap">
          <Button variant="secondary" onClick={() => setShowAssignDiet(true)}>
            {t('users.assignDietPlan', 'Assign Diet Plan')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowAssignExercise(true)}
          >
            {t('users.assignExercisePlan', 'Assign Exercise Plan')}
          </Button>
          <Button variant="secondary" onClick={() => setShowAddPayment(true)}>
            {t('users.addPayment', 'Add Payment')}
          </Button>
          <Button
            variant="ghost"
            onClick={() => setQrUser({ id: user._id, name: user.name })}
          >
            <QrCode className="h-4 w-4 mr-2" />
            {t('users.viewQrCode', 'View QR Code')}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">{t('users.details')}</TabsTrigger>
          <TabsTrigger value="checkins">
            {t('users.checkInHistory', 'Check-ins')}
          </TabsTrigger>
          <TabsTrigger value="health">{t('health.tab', 'Health')}</TabsTrigger>
          <TabsTrigger value="exercisePlans">
            {t('users.exercisePlans')}
          </TabsTrigger>
          <TabsTrigger value="payments">{t('users.payments')}</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('users.userDetails')}</CardTitle>
            </CardHeader>
            <CardContent>
              <UserDetails
                user={user}
                onUserUpdated={() => {
                  // Refresh user data after update
                  apiRequest({
                    method: 'GET',
                    url: `/users/${userId}`,
                  }).then((userData) => {
                    if (userData) {
                      setUser(userData);
                    }
                  });
                }}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="checkins" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>
                {t('users.checkInHistory', 'Check-in History')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <UserCheckInTable userId={userId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="health" className="mt-4">
          <UserHealthDashboard userId={userId} />
        </TabsContent>

        <TabsContent value="exercisePlans" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('users.userExercisePlans')}</CardTitle>
            </CardHeader>
            <CardContent>
              <UserExercisePlans
                userId={userId}
                exercisePlans={user.exercisePlans || []}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('users.userPayments')}</CardTitle>
            </CardHeader>
            <CardContent>
              <UserPayments
                userId={userId}
                payments={user.payments || []}
                onPaymentUpdate={fetchUser}
                user={user}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      <AssignDietPlanV2Modal
        userId={userId}
        open={showAssignDiet}
        onOpenChange={setShowAssignDiet}
        onAssigned={async () => {
          const userData = await apiRequest({
            method: 'GET',
            url: `/users/${userId}`,
          });
          if (userData) setUser(userData);
        }}
      />
      <AssignPlanModal
        planType="exercise"
        userId={userId}
        open={showAssignExercise}
        onOpenChange={setShowAssignExercise}
        onUserAssigned={async () => {
          // Refetch user after assignment
          const userData = await apiRequest({
            method: 'GET',
            url: `/users/${userId}`,
          });
          if (userData) setUser(userData);
        }}
      />
      <AddPaymentDialog
        open={showAddPayment}
        onOpenChange={async (open) => {
          setShowAddPayment(open);
          if (!open) {
            // Refetch user after payment dialog closes
            const userData = await apiRequest({
              method: 'GET',
              url: `/users/${userId}`,
            });
            if (userData) setUser(userData);
          }
        }}
        userId={userId}
      />
      {qrUser && (
        <LoginQRDialog
          userId={qrUser.id}
          userName={qrUser.name}
          open={!!qrUser}
          onOpenChange={(open) => {
            if (!open) setQrUser(null);
          }}
        />
      )}
    </div>
  );
}

export default function UserDetailPage() {
  return (
    <Suspense
      fallback={
        <div className="flex justify-center items-center h-40">Loading...</div>
      }
    >
      <UserDetailContent />
    </Suspense>
  );
}
